
import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building, CalendarDays, Home, Settings, BarChart, FileCheck, LogOut, User, Hammer, ShieldAlert, UserCheck, Users, Calculator, PoundSterling, Euro, DollarSign, Contact, Briefcase, TrendingUp, Plus, FolderOpen, MapPin, Search, MessageSquare, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { useUserCountry } from '@/hooks/useUserCountry';

import { 
  Sidebar, SidebarContent, SidebarMenu, SidebarMenuButton, 
  SidebarMenuItem, SidebarGroup, SidebarFooter, SidebarHeader
} from "@/components/ui/sidebar";
import { useToast } from '@/hooks/use-toast';
import { useResponsive } from '@/hooks/useResponsive';

// Get region-specific finance icon
const getFinanceIcon = (country: string) => {
  switch (country) {
    case 'GB': return PoundSterling;
    case 'FR':
    case 'DE':
    case 'ES':
    case 'IT': return Euro;
    case 'US': return DollarSign;
    default: return BarChart;
  }
};

const AppSidebar = () => {
  const location = useLocation();
  const { currentUser, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMobile } = useResponsive();
  const { userCountry } = useUserCountry();
  
  const FinanceIcon = getFinanceIcon(userCountry || 'GB');
  
  // Debug logging for admin status
  useEffect(() => {
    console.log("AppSidebar - Current User:", currentUser?.email);
    console.log("AppSidebar - Is Admin:", isAdmin);
  }, [currentUser, isAdmin]);
  
  const handleLogout = () => {
    logout();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
    navigate('/login');
  };

  // Extract user's name from metadata or use email
  const userMetadata = currentUser?.user_metadata;
  const displayName = userMetadata ? 
    `${userMetadata.firstName || ''} ${userMetadata.lastName || ''}`.trim() : 
    currentUser?.email || 'User';

  // Define menu items - Fixed Area Intel path
  const menuItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard', color: 'text-blue-600' },
    { path: '/properties', icon: Building, label: 'Properties', color: 'text-green-600' },
    { path: '/landlords', icon: Users, label: 'Landlords', color: 'text-purple-600' },
    { path: '/new-project', icon: FolderOpen, label: 'Project Management', color: 'text-orange-600' },
    { path: '/deal-search', icon: Search, label: 'Deal Search', color: 'text-emerald-500', comingSoon: true },
    { path: '/investment-analyser', icon: Calculator, label: 'Investment Analyser', color: 'text-indigo-600' },
    { path: '/rent-trends', icon: TrendingUp, label: 'Rent Trends & Refinance', color: 'text-emerald-600' },
    { path: '/area-intelligence', icon: MapPin, label: 'Area Intel', color: 'text-blue-500' },
    { path: '/calendar', icon: CalendarDays, label: 'Calendar', color: 'text-red-600' },
    { path: '/finance', icon: FinanceIcon, label: 'Finance', color: 'text-yellow-600' },
    { path: '/user-contacts', icon: Contact, label: 'My Contacts', color: 'text-pink-600' },
    { path: '/contacts', icon: Briefcase, label: 'Professional Directory', color: 'text-teal-600' },
    { path: '/references', icon: UserCheck, label: 'Reference Check', color: 'text-cyan-600' },
    { path: '/compliance', icon: FileCheck, label: 'Compliance', color: 'text-amber-600' },
    { path: '/community', icon: MessageSquare, label: 'Community', color: 'text-violet-600' },
    { path: '/team', icon: Users, label: 'Team', color: 'text-sky-600' },
    { path: '/settings', icon: Settings, label: 'Settings', color: 'text-gray-600' }
  ];

  // Admin menu item
  const adminMenuItem = { path: '/admin', icon: ShieldAlert, label: 'Admin Panel', color: 'text-amber-500' };

  return (
    <Sidebar className="h-screen flex flex-col bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 border-r border-slate-200 dark:border-slate-700 shadow-lg">
      <SidebarHeader className="flex flex-col items-center px-4 py-6 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-700">
        <Link to="/dashboard" className="flex flex-col items-center justify-center mb-2 group">
          <div className="p-2 rounded-xl bg-white dark:bg-slate-800 shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-105">
            <img 
              src="/lovable-uploads/924a6ca1-11fb-46d9-a66b-f6b94a0c23c6.png" 
              alt="ProptPilot Logo" 
              className="w-[120px] max-w-full h-auto logo-sidebar"
              style={{ objectFit: 'contain' }}
            />
          </div>
        </Link>
      </SidebarHeader>
      
      <SidebarContent className="flex-1 overflow-y-auto px-2 py-4">
        <SidebarGroup>
          <SidebarMenu className="space-y-1">
            {menuItems.map((item) => (
              <SidebarMenuItem key={item.path}>
                {item.comingSoon ? (
                  <SidebarMenuButton
                    className="touch-input rounded-xl opacity-50 cursor-not-allowed"
                    disabled
                  >
                    <div className={`p-1.5 rounded-lg ${item.color} bg-white dark:bg-slate-800 shadow-sm`}>
                      <item.icon className="h-4 w-4" />
                    </div>
                    <span className="font-medium text-slate-400 dark:text-slate-500">
                      {item.label}
                    </span>
                    <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded-full">
                      Soon
                    </span>
                  </SidebarMenuButton>
                ) : (
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === item.path}
                    className={`touch-input rounded-xl transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-slate-700 dark:hover:to-slate-600 hover:shadow-sm ${
                      location.pathname === item.path 
                        ? 'bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 shadow-md border border-blue-200 dark:border-blue-700' 
                        : 'hover:scale-[1.02]'
                    }`}
                  >
                    <Link to={item.path} className="flex items-center gap-3 w-full">
                      <div className={`p-1.5 rounded-lg ${item.color} bg-white dark:bg-slate-800 shadow-sm`}>
                        <item.icon className="h-4 w-4" />
                      </div>
                      <span className={`font-medium ${location.pathname === item.path ? 'text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-300'}`}>
                        {item.label}
                      </span>
                    </Link>
                  </SidebarMenuButton>
                )}
              </SidebarMenuItem>
            ))}
            
            {/* Show Admin panel link only to admins */}
            {isAdmin && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname === adminMenuItem.path}
                  className={`touch-input rounded-xl transition-all duration-200 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 hover:from-amber-100 hover:to-orange-100 dark:hover:from-amber-800/30 dark:hover:to-orange-800/30 border border-amber-200 dark:border-amber-700 hover:shadow-md hover:scale-[1.02] ${
                    location.pathname === adminMenuItem.path ? 'shadow-md' : ''
                  }`}
                >
                  <Link to={adminMenuItem.path} className="flex items-center gap-3 w-full">
                    <div className={`p-1.5 rounded-lg ${adminMenuItem.color} bg-white dark:bg-slate-800 shadow-sm`}>
                      <adminMenuItem.icon className="h-4 w-4" />
                    </div>
                    <span className="font-medium text-amber-700 dark:text-amber-300">
                      {adminMenuItem.label}
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {/* Logout button */}
            <SidebarMenuItem className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <SidebarMenuButton
                onClick={handleLogout}
                className="touch-input rounded-xl text-muted-foreground hover:text-red-600 dark:hover:text-red-400 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 dark:hover:from-red-900/20 dark:hover:to-pink-900/20 transition-all duration-200 hover:shadow-sm hover:scale-[1.02]"
              >
                <div className="p-1.5 rounded-lg text-red-500 bg-white dark:bg-slate-800 shadow-sm">
                  <LogOut className="h-4 w-4" />
                </div>
                <span className="font-medium">Log out</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="px-4 py-4 border-t border-slate-200 dark:border-slate-700 mt-auto bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-800 dark:to-slate-700">
        <div className="flex flex-col space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-600">
            <div className="p-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 shadow-sm">
              <User className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[150px]">
                {displayName}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400 truncate max-w-[180px]">
                {currentUser?.email}
                {isAdmin && <span className="ml-2 text-amber-600 dark:text-amber-400 font-medium">(Admin)</span>}
              </div>
            </div>
          </div>
          <div className="text-xs text-center text-slate-500 dark:text-slate-400 font-medium">
            Version 1.0
          </div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

// Export menuItems for use in other components - Fixed Area Intel path
export const menuItems = [
  { path: '/dashboard', icon: Home, label: 'Dashboard' },
  { path: '/properties', icon: Building, label: 'Properties' },
  { path: '/landlords', icon: Users, label: 'Landlords' },
  { path: '/new-project', icon: FolderOpen, label: 'Project Management' },
  { path: '/deal-search', icon: Search, label: 'Deal Search', comingSoon: true },
  { path: '/investment-analyser', icon: Calculator, label: 'Investment Analyser' },
  { path: '/rent-trends', icon: TrendingUp, label: 'Rent Trends & Refinance' },
  { path: '/area-intelligence', icon: MapPin, label: 'Area Intel' },
  { path: '/calendar', icon: CalendarDays, label: 'Calendar' },
  { path: '/finance', icon: BarChart, label: 'Finance' },
  { path: '/user-contacts', icon: Contact, label: 'My Contacts' },
  { path: '/contacts', icon: Briefcase, label: 'Professional Directory' },
  { path: '/references', icon: UserCheck, label: 'Reference Check' },
  { path: '/compliance', icon: FileCheck, label: 'Compliance' },
  { path: '/community', icon: MessageSquare, label: 'Community' },
  { path: '/team', icon: Users, label: 'Team' },
  { path: '/settings', icon: Settings, label: 'Settings' }
];

export default AppSidebar;
